import { Spacing, Typography } from '@persona-id/design-tokens';
import { kebabCase } from 'lodash/fp';
import { DateTime } from 'luxon';
import { ComponentProps, memo, useCallback, useMemo, useRef, useState } from 'react';
import styled from 'styled-components';

import useIsITEInternal from 'dashboard/apps/InquiryTemplateEditor/hooks/useIsITEInternal';
import InquiriesGatingSticker, {
  EnhancedGatingStickerType,
} from 'dashboard/apps/InquiryTemplateEditor/InquiriesGatingSticker';
import ContextMenu from 'dashboard/components/ContextMenu';
import FeatureFlagged from 'dashboard/components/FeatureFlagged';
import HoverCopyableField from 'dashboard/components/HoverCopyableField';
import ModalWrapper from 'dashboard/components/ModalWrapper';
import { MonoSpan } from 'dashboard/components/Typography';
import ConfirmationModal from 'dashboard/components-lib/ConfirmationModal';
import PlatformConnectionsModalContent from 'dashboard/components-lib/PlatformConnectionsModalContent';
import TableLayout, {
  TableActionTD,
  TableLinkTR,
  TableNullState,
  TableTD,
  TableTH,
  TableTR,
} from 'dashboard/layouts/TableLayout';
import { SortParams } from 'dashboard/lib/interfaces/SortParams';
import { Environment } from 'dashboard/models/environment';
import {
  InquiryTemplate,
  InquiryTemplateFilterOption,
  InquiryTemplateStatus,
  InquiryTemplateTier,
} from 'dashboard/models/inquiry-template';
import { ProductFeatureName } from 'dashboard/models/organization';
import { TemplateFilterOption, TemplateListPartial } from 'dashboard/models/template';
import Input from 'design-system/components/Input';
import Spacer from 'design-system/components/Spacer';
import Sticker from 'design-system/components/Sticker';
import { Paragraph } from 'design-system/components/Text';
import { SegmentClient } from 'lib/clients/SegmentClient';
import { DATE_AND_TIME_FORMAT, TIMEZONE_NAME_ABBREVIATED } from 'lib/constants/luxonFormats';
import useConfirmation from 'lib/hooks/useConfirmation';
import useFeatureFlag from 'lib/hooks/useFeatureFlag';
import usePermission from 'lib/hooks/usePermission';
import useProductFeature from 'lib/hooks/useProductFeature';
import { ApiModel } from 'lib/interfaces/api';
import { Permission } from 'lib/permissions';

enum ModalActionTypes {
  Activate = 'activate',
  Deactivate = 'deactivate',
  Clone = 'clone',
}

interface ModalActionConfig {
  inquiryTemplate: InquiryTemplate;
  modalActionType: ModalActionTypes;
}

interface InquiryTemplatesTableProps {
  inquiryTemplates: Array<TemplateListPartial | InquiryTemplate>;
  legacyTemplateFilters: Array<TemplateFilterOption>;
  inquiryTemplateFilters: Array<InquiryTemplateFilterOption>;
  sortParams: SortParams;
  currentEnvironmentType: Environment['type'];
  canSort: boolean;
  hasUsedFilters: boolean;
  isLoading: boolean;
  handleSortUpdate: (sortParams: SortParams) => void;
  handlePreviewInquiry: (
    inquiryTemplateId: string,
    inquiryTemplateVersionId: string,
    environmentName?: string,
  ) => void;
  handlePreviewLegacyInquiry: (templateId: string) => void;
  handleActivateInquiryTemplate: (inquiryTemplateId: string) => void;
  handleDeactivateInquiryTemplate: (inquiryTemplateId: string) => void;
  handleCloneInquiryTemplate: (inquiryTemplateId: string, name: string | undefined) => void;
}

const InquiryTemplatesTable = ({
  inquiryTemplates,
  legacyTemplateFilters,
  inquiryTemplateFilters,
  sortParams,
  currentEnvironmentType,
  canSort,
  hasUsedFilters,
  isLoading,
  handleSortUpdate,
  handlePreviewInquiry,
  handlePreviewLegacyInquiry,
  handleActivateInquiryTemplate,
  handleDeactivateInquiryTemplate,
  handleCloneInquiryTemplate,
}: InquiryTemplatesTableProps) => {
  const hasTemplateViewPermission = usePermission(Permission.InquiryTemplateView);
  const hasTemplateWritePermission = usePermission(Permission.InquiryTemplateWrite);
  const hasInquiryWritePermission = usePermission(Permission.InquiryWrite);
  const hasInquiriesEnhanced = useProductFeature(ProductFeatureName.InquiriesEnhanced);
  const isITEInternal = useIsITEInternal();
  const isInquiryInspectEnabled = useFeatureFlag('dashboard:fle-inquiry-inspect');

  const [shouldShowConfirmationModal, { prompt, confirm, cancel }] = useConfirmation();
  const [templateForModalAction, setTemplateForModalAction] = useState<ModalActionConfig | null>(
    null,
  );

  const templateName = useRef<string | undefined>();
  const [clonedTemplateName, setClonedTemplateName] = useState<string>();

  const handleColumnHeaderClick = (columnName: string) => {
    if (!canSort) {
      return;
    }

    handleSortUpdate({
      sort: columnName,
      shouldSortDescending:
        columnName === sortParams.sort ? !sortParams.shouldSortDescending : true,
    });
  };

  const handleModalActionClick = useCallback(
    (inquiryTemplate: InquiryTemplate, modalActionType: ModalActionTypes) => {
      setTemplateForModalAction({
        inquiryTemplate,
        modalActionType,
      });
      prompt(() => {
        switch (modalActionType) {
          case ModalActionTypes.Deactivate:
            handleDeactivateInquiryTemplate(inquiryTemplate.id);
            break;
          case ModalActionTypes.Activate:
            handleActivateInquiryTemplate(inquiryTemplate.id);
            break;
          case ModalActionTypes.Clone:
            handleCloneInquiryTemplate(inquiryTemplate.id, templateName.current);
            break;
        }
        setTemplateForModalAction(null);
      });
    },
    [
      prompt,
      templateForModalAction,
      handleDeactivateInquiryTemplate,
      handleActivateInquiryTemplate,
      handleCloneInquiryTemplate,
      templateName.current,
    ],
  );

  const inquiryTemplateRows = useMemo(() => {
    if (!hasTemplateViewPermission) {
      return legacyTemplateFilters
        .map((legacyTemplateFilter) => {
          const { id, name, 'updated-at': updatedAt } = legacyTemplateFilter;
          return (
            <TableTR key={id}>
              {/* @ts-expect-error [strict-migration] The following line is not strict; please fix! */}
              <LegacyTemplateRow key={id} id={id} name={name} updatedAt={updatedAt} />{' '}
            </TableTR>
          );
        })
        .concat(
          inquiryTemplateFilters.map((inquiryTemplateFilter) => {
            const {
              id,
              name,
              description,
              status,
              'updated-at': updatedAt,
            } = inquiryTemplateFilter;
            return (
              <TableTR key={id}>
                <InquiryTemplateRow
                  id={id}
                  name={name}
                  description={description}
                  publishedAt={updatedAt}
                  isActive={status === InquiryTemplateStatus.Active}
                />
              </TableTR>
            );
          }),
        );
    } else {
      return inquiryTemplates.map((inquiryTemplate) => {
        if (inquiryTemplate.type === ApiModel.Template) {
          const {
            id,
            attributes: { name, 'updated-at': updatedAt },
          } = inquiryTemplate;

          return (
            <TableLinkTR
              key={id}
              to={`/dashboard/inquiry-templates/${id}`}
              data-marketing-id='legacy-template-row'
              data-name={kebabCase(name)}
            >
              <LegacyTemplateRow
                id={id}
                name={name}
                updatedAt={updatedAt}
                actions={
                  <TemplateRowActions
                    id={id}
                    items={[
                      {
                        id: 'preview',
                        variant: 'basic',
                        label: 'Preview',
                        hidden: false,
                        onClick: () => handlePreviewLegacyInquiry(id),
                      },
                    ]}
                  />
                }
              />
            </TableLinkTR>
          );
        } else {
          const {
            id,
            attributes: {
              name,
              description,
              status,
              'pricing-tier': pricingTier,
              'latest-published-at': latestPublishedAt,
            },
            relationships,
          } = inquiryTemplate;

          const isActive = status === InquiryTemplateStatus.Active;

          // always show draft by default if present
          const draftVersionId = relationships['latest-draft-version'].data?.id;
          const publishedVersionId = relationships['latest-published-version'].data?.id;
          const templateVersionId = draftVersionId ? draftVersionId : publishedVersionId;

          const previewInquiry = (environmentName?: string) => {
            SegmentClient.sendSegmentTrackEvent('Flow Editor - Select Preview From Index Page', {
              'inquiry-template': id,
            });
            /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
            handlePreviewInquiry(id, templateVersionId, environmentName);
          };

          return (
            <TableLinkTR
              key={id}
              to={`/dashboard/inquiry-templates/${id}/${templateVersionId}/flow`}
              data-marketing-id='inquiry-template-row'
              data-name={kebabCase(name)}
            >
              <InquiryTemplateRow
                id={id}
                name={name}
                description={description}
                publishedAt={latestPublishedAt}
                actions={
                  <TemplateRowActions
                    id={id}
                    items={[
                      {
                        id: 'preview',
                        variant: 'basic',
                        label: 'Preview',
                        hidden:
                          !isActive ||
                          !hasInquiryWritePermission ||
                          !hasTemplateViewPermission ||
                          currentEnvironmentType !== ApiModel.EnvironmentSandbox,
                        onClick: () => previewInquiry(),
                      },
                      {
                        id: 'inspect',
                        variant: 'basic',
                        label: 'Inspect',
                        hidden:
                          !isITEInternal ||
                          !hasInquiryWritePermission ||
                          !hasTemplateViewPermission ||
                          !isInquiryInspectEnabled ||
                          !isActive ||
                          publishedVersionId == null,
                        onClick: () => {
                          window.open(
                            `/dashboard/inquiry-templates/${inquiryTemplate.id}/${publishedVersionId}/inspect`,
                            '_blank',
                          );
                        },
                      },
                      {
                        id: 'activate',
                        variant: 'basic',
                        label: 'Activate',
                        hidden: !hasTemplateWritePermission || !hasInquiriesEnhanced || isActive,
                        onClick: () =>
                          handleModalActionClick(inquiryTemplate, ModalActionTypes.Activate),
                      },
                      {
                        id: 'clone',
                        variant: 'basic',
                        label: 'Make a copy',
                        hidden: !hasTemplateWritePermission || !isActive,
                        onClick: () => {
                          templateName.current = `Copy of ${inquiryTemplate.attributes?.name}`;
                          setClonedTemplateName(templateName.current);
                          handleModalActionClick(inquiryTemplate, ModalActionTypes.Clone);
                        },
                      },
                      {
                        id: 'deactivate',
                        variant: 'danger',
                        label: 'Deactivate',
                        hidden: !hasTemplateWritePermission || !isActive,
                        onClick: () =>
                          handleModalActionClick(inquiryTemplate, ModalActionTypes.Deactivate),
                      },
                    ]}
                  />
                }
                isActive={isActive}
                isEnhanced={hasInquiriesEnhanced && pricingTier === InquiryTemplateTier.Enhanced}
              />
            </TableLinkTR>
          );
        }
      });
    }
  }, [
    hasTemplateViewPermission,
    hasTemplateWritePermission,
    hasInquiryWritePermission,
    legacyTemplateFilters,
    inquiryTemplateFilters,
    inquiryTemplates,
    handlePreviewLegacyInquiry,
    currentEnvironmentType,
    hasInquiriesEnhanced,
    handlePreviewInquiry,
    handleModalActionClick,
  ]);

  const modalActionName: string = useMemo(() => {
    switch (templateForModalAction?.modalActionType) {
      case ModalActionTypes.Deactivate:
        return 'Deactivate';
      case ModalActionTypes.Activate:
        return 'Activate';
      case ModalActionTypes.Clone:
        return 'Make a copy';
      default:
        return '';
    }
  }, [templateForModalAction?.modalActionType]);

  return (
    <>
      <ModalWrapper isOpen={shouldShowConfirmationModal} handleCloseModal={cancel}>
        <ConfirmationModal
          size='lg'
          header={
            templateForModalAction?.modalActionType === ModalActionTypes.Clone
              ? `${modalActionName} of this template?`
              : `${modalActionName} this template?`
          }
          confirmActionText={modalActionName}
          confirmButtonType='primary'
          cancelAction={cancel}
          confirmAction={confirm}
        >
          {templateForModalAction?.modalActionType === ModalActionTypes.Clone ? (
            <>
              <Paragraph>
                This will make a new draft copy of{' '}
                <strong>{templateForModalAction?.inquiryTemplate.attributes?.name}</strong>{' '}
                template&apos;s latest version.
              </Paragraph>
              <Spacer height={3} />
              <Paragraph weight='bold' color='primary'>
                Name
              </Paragraph>
              <Spacer height={1} />
              <Input
                value={clonedTemplateName}
                onChange={(e) => {
                  setClonedTemplateName(e.target.value);
                  templateName.current = e.target.value;
                }}
              />
            </>
          ) : (
            <Paragraph style={{ whiteSpace: 'pre-wrap' }}>
              <strong>
                {templateForModalAction?.inquiryTemplate.attributes?.name || 'Template'}
              </strong>
              {` will be ${modalActionName.toLowerCase()}d.`}
              <Spacer height={1} />
              <FeatureFlagged featureFlag='global:enable-platform-dependency-modal'>
                {templateForModalAction?.inquiryTemplate.id &&
                  templateForModalAction?.modalActionType === ModalActionTypes.Deactivate && (
                    <PlatformConnectionsModalContent
                      platformNodeId={templateForModalAction?.inquiryTemplate.id}
                    />
                  )}
              </FeatureFlagged>
              {!hasInquiriesEnhanced &&
                `\n\nAre you sure you want to proceed? This action cannot be undone.`}
            </Paragraph>
          )}
        </ConfirmationModal>
      </ModalWrapper>
      <TableLayout
        dataTest='inquiry-templates-table'
        showHeader={hasUsedFilters}
        isLoading={isLoading}
        isEmpty={
          !hasTemplateViewPermission
            ? legacyTemplateFilters.length === 0 && inquiryTemplateFilters.length === 0
            : inquiryTemplates.length === 0
        }
        containerMaxHeight={720}
        nullState={
          <TableNullState hasUsedFilters={hasUsedFilters} dataTestBlock='inquiry-templates-table'>
            <h3>Looks like there aren&#39;t any inquiry templates yet.</h3>
            <p>Create an inquiry template to get started.</p>
          </TableNullState>
        }
        headerTR={
          <>
            <TableTH
              id='inquiry-templates-table__name-header'
              onClick={() => handleColumnHeaderClick('name')}
              canSort={canSort}
              isSorted={sortParams.sort === 'name'}
              isDescending={sortParams.shouldSortDescending}
              isSticky
            >
              Name
            </TableTH>
            <TableTH isSticky>ID</TableTH>
            <TableTH isSticky>Description</TableTH>
            <TableTH isSticky>Status</TableTH>
            <TableTH
              id='inquiry-templates-table__updated-at-header'
              onClick={() => handleColumnHeaderClick('updated_at')}
              canSort={canSort}
              isSorted={sortParams.sort === 'updated_at'}
              isDescending={sortParams.shouldSortDescending}
              isSticky
            >{`Last published at (${DateTime.local().toFormat(
              TIMEZONE_NAME_ABBREVIATED,
            )})`}</TableTH>
            <TableTH isSticky>{/* Actions column */}</TableTH>
          </>
        }
      >
        {inquiryTemplateRows}
      </TableLayout>
    </>
  );
};

const TemplateRowActions = ({
  id,
  items,
}: {
  id: string;
  items: ComponentProps<typeof ContextMenu>['items'];
}) => {
  return (
    <TableActionTD id={`table-action__${id}`} onClick={(e) => e.preventDefault()}>
      <ContextMenu
        data-test='action-menu'
        items={items}
        renderItem={({ id, variant, label, onClick }) => (
          <ContextMenu.Item
            key={id}
            variant={variant}
            id={`action-menu__${id}`}
            onClick={(e) => {
              // Needed to avoid triggering row click below
              e.stopPropagation();
              e.preventDefault();
              /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
              onClick();
            }}
          >
            {label}
          </ContextMenu.Item>
        )}
      />
    </TableActionTD>
  );
};

const TemplateID = styled(MonoSpan)`
  width: 140px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

interface LegacyTemplateRowProps {
  id: string;
  name: string;
  updatedAt: string;
  actions?: React.ReactNode;
}

const LegacyTemplateRow = memo(function LegacyTemplateRow({
  id,
  name,
  updatedAt,
  actions,
}: LegacyTemplateRowProps) {
  return (
    <>
      <TableTD>{name}</TableTD>
      <TableTD>
        <HoverCopyableField copyValue={id} confirmationText='Copied Template ID'>
          <TemplateID>{id}</TemplateID>
        </HoverCopyableField>
      </TableTD>
      <TableTD />
      <TableTD>
        <StatusSticker isActive={true} />
      </TableTD>
      <TableTD>
        {updatedAt ? DateTime.fromISO(updatedAt).toFormat(DATE_AND_TIME_FORMAT) : '-–'}
      </TableTD>
      {actions}
    </>
  );
});

const TemplateNameWrapper = styled.div`
  display: flex;
  align-items: center;
  gap: ${Spacing[2]};
  min-width: 300px;
`;

const TemplateDescription = styled(Paragraph).attrs(() => ({ size: 'sm' }))`
  display: -webkit-box;
  max-height: calc(${Typography.TextSmall.lineHeight} * 2);
  overflow: hidden;
  text-overflow: ellipsis;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
`;

interface InquiryTemplateRowProps {
  id: string;
  name: string;
  description: string | undefined;
  publishedAt: string | undefined;
  actions?: React.ReactNode;
  isActive: boolean;
  isEnhanced?: boolean;
}

const InquiryTemplateRow = memo(function InquiryTemplateRow({
  id,
  name,
  description,
  publishedAt,
  actions,
  isActive,
  isEnhanced,
}: InquiryTemplateRowProps) {
  return (
    <>
      <TableTD>
        <TemplateNameWrapper>
          {name}
          {isEnhanced && (
            <InquiriesGatingSticker
              dataMarketingIdSuffix='templates-table'
              stickerType={EnhancedGatingStickerType.Enhanced}
            />
          )}
        </TemplateNameWrapper>
      </TableTD>
      <TableTD>
        <HoverCopyableField copyValue={id} confirmationText='Copied Inquiry Template ID'>
          <TemplateID>{id}</TemplateID>
        </HoverCopyableField>
      </TableTD>
      <TableTD>
        <TemplateDescription>{description}</TemplateDescription>
      </TableTD>
      <TableTD>
        <StatusSticker isActive={isActive} />
      </TableTD>
      <TableTD>
        {publishedAt ? DateTime.fromISO(publishedAt).toFormat(DATE_AND_TIME_FORMAT) : '–-'}
      </TableTD>
      {actions}
    </>
  );
});

const StickerContainer = styled('div')`
  display: flex;
  flex-direction: row;
  justify-content: flex-start;
`;

const StatusSticker = ({ isActive }: { isActive: boolean }) => {
  return (
    <StickerContainer>
      {isActive ? <Sticker variant='success'>Active</Sticker> : <Sticker>Inactive</Sticker>}
    </StickerContainer>
  );
};

export default InquiryTemplatesTable;
